import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { N800, N80A, B100 } from '@atlaskit/theme/colors';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';

import SearchErrorGraphic from '../images/SearchError.svg';
import type { UnsplashImageSelectionComponentProps } from '../pageCoverPictureTypes';

import { downloadUnsplashPhoto } from './unsplashAPI';

const i18n = defineMessages({
	nothingMatchesSearch: {
		id: 'page-cover-picture.unsplash.search.not.found',
		description:
			'Text that shows up if no images are found matching search keyword in Unsplash API.',
		defaultMessage: 'Nothing matches your search',
	},
});

const artistInfoSelector = 'page-cover-picture.ArtistInfo';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArtistInfo = styled.div<{ 'data-styled-selector'?: string }>({
	background: token('color.background.neutral', N80A),
	borderBottomLeftRadius: '3px',
	borderBottomRightRadius: '3px',
	position: 'absolute',
	bottom: 0,
	width: '150px',
	fontSize: 'small',
	visibility: 'hidden',
	'&:hover': {
		visibility: 'visible',
	},
});
ArtistInfo.defaultProps = {
	'data-styled-selector': artistInfoSelector,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashImage = styled.img({
	height: '100px',
	width: '150px',
	borderRadius: '3px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ArtistNameWrapper = styled.div({
	marginLeft: token('space.050', '4px'),
	color: token('color.text.inverse', 'white'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashWrapper = styled.div({
	width: '150px',
	display: 'inline-grid',
	position: 'relative',
	'&:hover': {
		opacity: 0.8,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashSearchErrorWrapper = styled.div({
	gridColumn: 'span 3',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	height: '210px',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchErrorImage = styled.img({
	height: '100px',
	width: '110px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchErrorText = styled.div({
	fontWeight: 'bold',
	fontSize: '18px',
	marginTop: token('space.400', '32px'),
	color: token('color.text', N800),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashImageWrapper = styled.button({
	border: 'none',
	background: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	[`&:hover + [data-styled-selector="${artistInfoSelector}"]`]: {
		visibility: 'visible',
	},
	cursor: 'pointer',
	padding: 0,
	height: '100px',
	outlineColor: token('color.border.information', B100),
});

export const UnsplashImageSelection = ({
	unsplashInfoArray,
	handleUnsplashUpload,
	setIsUploadingPicture,
	closeModal,
	coverPictureState,
}: UnsplashImageSelectionComponentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { formatMessage } = useIntl();

	const isLivePage = useIsLivePage();

	const onUnsplashImageClick = (url, downloadLink) => {
		experienceTracker.start({
			name: COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
		});
		downloadUnsplashPhoto(downloadLink);
		handleUnsplashUpload(url);
		setIsUploadingPicture(true);
		void coverPictureState.setIsUploadingPicture(true);
		closeModal();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'added',
				actionSubject: 'image',
				actionSubjectId: 'headerImageAdded',
				source: 'editorPageCoverPicture',
				attributes: {
					imageSource: 'unsplash',
					imageId: url,
					inputMethod: 'header',
					isLivePage,
				},
			},
		}).fire();
	};

	const handleArtistUrlClick = (url) => {
		window.open(url, '_blank');
		return false;
	};

	if (unsplashInfoArray.length == 0) {
		return (
			<UnsplashSearchErrorWrapper data-testid="search-error-div">
				<SearchErrorImage src={SearchErrorGraphic} alt="search error image" />
				<SearchErrorText>{formatMessage(i18n.nothingMatchesSearch)}</SearchErrorText>
			</UnsplashSearchErrorWrapper>
		);
	}

	/* eslint-disable jsx-a11y/anchor-is-valid */
	return unsplashInfoArray.map((imageInfo: any, index) => {
		return (
			<UnsplashWrapper key={index}>
				<UnsplashImageWrapper
					onClick={() => {
						onUnsplashImageClick(imageInfo.fullSizeImageUrl, imageInfo.downloadLink);
					}}
				>
					<UnsplashImage
						data-testid={`unsplash-image-${index}`}
						src={imageInfo.smallSizeImageUrl}
						alt={imageInfo.description}
					/>
				</UnsplashImageWrapper>
				<ArtistInfo>
					<a
						href="#"
						onClick={() => {
							handleArtistUrlClick(imageInfo.artistUrl);
						}}
					>
						<ArtistNameWrapper>{imageInfo.artistName}</ArtistNameWrapper>
					</a>
				</ArtistInfo>
			</UnsplashWrapper>
		);
	});
};
